import styled, { keyframes } from 'styled-components';
import { AnimatedSpateAiIcon } from '@/ui/design-system/components';
import { BodyMedium } from 'components/Text';
import DesignCardSVG from './DesignCardSVG';
import Stack from 'components/Stack';

const CARD_WIDTH_PX = 237;
const CARD_GAP_PX = 20;
// Fallback only — the grid tells us its real cell height, which depends on how
// many metric rows the filters ask for.
const CARD_HEIGHT_PX = 252;

const BannerWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${({ theme }) => theme.gray_1};
    padding-left: 6px;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${CARD_WIDTH_PX}px, 1fr));
    gap: ${CARD_GAP_PX}px;
`;

const Card = styled.div`
    position: relative;
    width: 100%;
    height: ${({ $height }) => $height ?? `${CARD_HEIGHT_PX}px`};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    background: ${({ theme }) => theme.gray_7};
    border: 1px solid ${({ theme }) => theme.gray_6};
    overflow: hidden;
`;

const shimmer = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 100% 0; }
`;

const Shine = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;

    background-image: linear-gradient(
        230deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0) 48%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 52%,
        rgba(255, 255, 255, 0) 100%
    );
    background-position: 0 0;
    background-repeat: repeat-x;
    background-size: ${({ $shimmerWidth = '1000px' }) =>
        `${$shimmerWidth} 100%`};
    animation: ${shimmer} ${({ $dur = 1000 }) => `${$dur}ms`} ease-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

export const CardSkeletonItem = ({
    shimmerOn = true,
    shimmerWidth = '1000px',
    shimmerDuration = 1000,
}) => (
    <Card $height="100%">
        <DesignCardSVG />
        {shimmerOn && (
            <Shine $shimmerWidth={shimmerWidth} $dur={shimmerDuration} />
        )}
    </Card>
);

export const CardSkeletonGrid = ({
    count = 12,
    shimmerOn = true,
    shimmerWidth = '1000px',
    shimmerDuration = 1000,
    cardHeightPx,
}) => {
    const items = Array.from({ length: count });
    return (
        <CardGrid>
            {items.map((_, i) => (
                <Card key={i} $height={cardHeightPx && `${cardHeightPx}px`}>
                    <DesignCardSVG />
                    {shimmerOn && (
                        <Shine
                            $shimmerWidth={shimmerWidth}
                            $dur={shimmerDuration}
                        />
                    )}
                </Card>
            ))}
        </CardGrid>
    );
};

export const CardSkeleton = ({
    count = 12,
    shimmerOn = true,
    shimmerWidth = '1000px',
    shimmerDuration = 1000,
    cardHeightPx,
}) => {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <Stack
                css="padding: 4px 10px 24px;"
                $space="16px"
                $direction="column"
            >
                <BannerWrap>
                    <Stack $space="12px" $direction="row">
                        <AnimatedSpateAiIcon size={18} />
                        <BodyMedium>Loading...</BodyMedium>
                    </Stack>
                </BannerWrap>

                <CardSkeletonGrid
                    count={count}
                    shimmerOn={shimmerOn}
                    shimmerWidth={shimmerWidth}
                    shimmerDuration={shimmerDuration}
                    cardHeightPx={cardHeightPx}
                />
            </Stack>
        </div>
    );
};
